var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var ToolConfig_1;
import { ToolType, ToolVariable } from '@symon-ai/wisepipe-base-pipe';
import { Column } from '@symon-ai/wisepipe-common-data';
import { JSON, JsonConverter, JsonDiscriminatorProperty, JsonSchemaV2, MapConverter } from '@symon-ai/wisepipe-serialize';
import './customFormat'; // force import for JsonSchemaV2
import { ColumnFormat, CustomFormat, DefaultSettings } from './customFormat';
import { getRenamedColumns } from './toolConfigUtil';
import { ToolScope } from './toolScope';
import { ToolScopeBuilder } from './toolScopeBuilder';
import { ToolValidationResult, ToolValidationState } from './validation/toolValidationResult';
import { toolConfigSchemaValidation } from '../validations.generated';
export const toolConfigSchema = {
    $id: 'urn:wisepipe-common-pipe:ToolConfig',
    type: 'object',
    properties: {
        toolType: {
            type: 'string',
            // build a list of valid tooltypes, except ToolType.NONE
            enum: Object.keys(ToolType)
                .map((key) => ToolType[key])
                .filter((key) => typeof key !== 'function' && key !== ToolType.NONE)
        },
        customFormats: {
            type: 'object',
            additionalProperties: { $ref: 'urn:wisepipe-common-pipe:CustomFormat' }
        }
    },
    required: ['toolType']
};
let ToolConfig = ToolConfig_1 = class ToolConfig {
    constructor(toolType, customFormats) {
        this.toolType = toolType;
        this.customFormats = customFormats;
        JSON.validate(this, ToolConfig_1);
    }
    // eslint-disable-next-line class-methods-use-this
    get hasModel() {
        return false;
    }
    static get info() {
        throw new Error(`Implement info() on each toolConfig.`);
    }
    static getStringValue(parameters, property) {
        if (property instanceof ToolVariable) {
            return property.getValue(parameters);
        }
        else {
            return property;
        }
    }
    applyParameters(parameters) {
        return this;
    }
    getBaseValues(parameters) {
        return this;
    }
    getOutputForTrainableConfig(ins, inPort, outPort, toolType, labelColumn, labelColumnType, excludeColumns, addProbability = false) {
        const tsb = new ToolScopeBuilder(ins);
        const source = ins.schemas.at(inPort);
        if (source) {
            const exp = tsb.export(outPort);
            // calculate the set of dependent columns, which is all inputs, except label column + exclude columns
            const excludeSet = new Set();
            excludeSet.add(labelColumn);
            if (excludeColumns)
                excludeColumns.forEach((col) => excludeSet.add(col));
            const dependents = [];
            const columnNames = [];
            source.forEach((colMet) => {
                columnNames.push(colMet.name);
                const excluded = excludeSet.has(colMet.name);
                if (excluded) {
                    exp.copy(inPort, colMet, false, false);
                }
                else {
                    dependents.push(colMet);
                    exp.copy(inPort, colMet, false, true);
                }
            });
            const newColumnFormats = new Map();
            const predictionColumnName = getRenamedColumns(columnNames, 'Prediction', toolType);
            // label type = null when its a calc
            // label type != null when its a run
            let labelType = labelColumnType;
            if (!labelType) {
                const srcColumn = exp.findSourceColumn(inPort, labelColumn);
                labelType = srcColumn.type;
                if (srcColumn.customFormat) {
                    newColumnFormats.set(predictionColumnName, srcColumn.customFormat.copyWithAutoGeneratedTrue());
                }
            }
            // use makeNew instead of findCopy because we don't want to copy the label's colOrigin into new prediction column
            exp.makeNew(new Column(predictionColumnName, labelType), dependents, true);
            if (addProbability) {
                const probabilityColumnName = getRenamedColumns(columnNames, 'Probability', toolType);
                exp.makeNew(new Column(probabilityColumnName, 'number'), dependents, true);
                newColumnFormats.set(probabilityColumnName, new CustomFormat(ColumnFormat.Percentage, 0, DefaultSettings.thousandSeparator, true));
            }
            exp.initializeColumnFormats().setColumnFormats(newColumnFormats).setColumnFormats(this.customFormats);
        }
        return tsb.build();
    }
    getOutputForTextClassifier(ins, inPort, outPort, labelColumn, textColumn, labelColumnType) {
        const tsb = new ToolScopeBuilder(ins);
        const source = ins.schemas.at(inPort);
        const exp = tsb.export(outPort);
        if (source) {
            const dependents = [];
            const columnNames = [];
            source.forEach((colMet) => {
                columnNames.push(colMet.name);
                if (colMet.name === textColumn) {
                    dependents.push(colMet);
                    exp.copy(inPort, colMet, false, true);
                }
                else {
                    exp.copy(inPort, colMet, false, false);
                }
            });
            const newColumnFormats = new Map();
            // label type = null when its a calc
            // label type != null when its a run
            let labelType = labelColumnType;
            if (!labelType) {
                const srcColumn = exp.findSourceColumn(inPort, labelColumn);
                labelType = srcColumn.type;
                if (srcColumn.customFormat) {
                    newColumnFormats.set('Prediction', srcColumn.customFormat.copyWithAutoGeneratedTrue());
                }
            }
            const predictionColumnName = getRenamedColumns(columnNames, 'Prediction', ToolType.TEXT_CLASSIFIER);
            const probabilityColumnName = getRenamedColumns(columnNames, 'Probability', ToolType.TEXT_CLASSIFIER);
            // use makeNew instead of findCopy because we don't want to copy the label's colOrigin into new prediction column
            exp.makeNew(new Column(predictionColumnName, labelType), dependents, true);
            exp.makeNew(new Column(probabilityColumnName, 'number'), dependents, true);
            newColumnFormats.set(probabilityColumnName, new CustomFormat(ColumnFormat.Percentage, 0, DefaultSettings.thousandSeparator, true));
            exp.initializeColumnFormats().setColumnFormats(newColumnFormats).setColumnFormats(this.customFormats);
        }
        return tsb.build();
    }
    getOutputForForecastConfig(ins, inPort, outPort, dateColumn, valueColumns, aliases, uncertainty, groupByColumns) {
        const tsb = new ToolScopeBuilder(ins);
        const source = ins.schemas.at(inPort);
        if (source) {
            this.getExportsForForecastConfig(tsb, inPort, outPort, dateColumn, valueColumns, aliases, uncertainty, groupByColumns);
        }
        return tsb.build();
    }
    getExportsForForecastConfig(tsb, inPort, outPort, dateColumn, valueColumns, aliases, uncertainty, groupByColumns) {
        const exp = tsb.export(outPort);
        const newColumnFormats = new Map();
        // calculate dependent parent columns.  forecast only "uses" date + val, but you can grp by val so add those too
        const dependents = [];
        const dateSourceCol = exp.findSourceColumn(inPort, dateColumn);
        if (dateSourceCol)
            dependents.push(dateSourceCol);
        valueColumns.forEach((valueColumn) => dependents.push(exp.findSourceColumn(inPort, valueColumn)));
        dependents.push(...this.getGroupByDependents(exp, inPort, groupByColumns, dependents));
        exp.makeNew(new Column(dateColumn, 'date'), dependents, true);
        const srcColumn = exp.findSourceColumn(inPort, dateColumn);
        if (srcColumn?.customFormat) {
            newColumnFormats.set(dateColumn, srcColumn.customFormat.copyWithInheritedTrue());
        }
        for (let i = 0; i < valueColumns.length; i++) {
            const srcColumn = exp.findSourceColumn(inPort, valueColumns[i]);
            exp.makeNew(new Column(valueColumns[i], 'number'), dependents, true);
            exp.makeNew(new Column(aliases[i], 'number'), dependents, true);
            if (srcColumn.customFormat) {
                newColumnFormats.set(valueColumns[i], srcColumn.customFormat.copyWithInheritedTrue());
                newColumnFormats.set(aliases[i], srcColumn.customFormat.copyWithAutoGeneratedTrue());
            }
            if (uncertainty) {
                exp.makeNew(new Column(`${aliases[i]} Upper`, 'number'), dependents, true);
                exp.makeNew(new Column(`${aliases[i]} Lower`, 'number'), dependents, true);
                if (srcColumn.customFormat) {
                    newColumnFormats.set(`${aliases[i]} Upper`, srcColumn.customFormat.copyWithAutoGeneratedTrue());
                    newColumnFormats.set(`${aliases[i]} Lower`, srcColumn.customFormat.copyWithAutoGeneratedTrue());
                }
            }
        }
        exp.makeNew(new Column('IsForecast', 'boolean'), dependents, true);
        exp.initializeColumnFormats().setColumnFormats(newColumnFormats).setColumnFormats(this.customFormats);
        return exp;
    }
    getOutputForRunningConfig(iConfigHelper, ins, inPort, outPort, valueColumns, groupColumns, orderColumn, runningValOutputColNameFn) {
        const tsb = new ToolScopeBuilder(ins);
        const source = ins.schemas.at(inPort);
        const exp = tsb.export(outPort);
        const orderColNames = orderColumn.map((col) => col.getBaseValues(iConfigHelper.getParameters()).name);
        if (source) {
            // valueCols are not common dependents
            const commonDepNames = [...groupColumns, ...Array.from(orderColNames)];
            this.copyAllInsAndReturnDependents(exp, inPort, source, [...valueColumns, ...commonDepNames]);
            const commonDependents = this.getDependents(exp, inPort, commonDepNames);
            const newColumnFormats = new Map();
            valueColumns.forEach((c) => {
                const valColMet = exp.findSourceColumn(inPort, c);
                const outputColumnName = runningValOutputColNameFn(c);
                exp.makeNew(new Column(outputColumnName, valColMet.type), [...commonDependents, valColMet], true);
                if (valColMet.customFormat) {
                    newColumnFormats.set(outputColumnName, valColMet.customFormat.copyWithAutoGeneratedTrue());
                }
            });
            exp.initializeColumnFormats().setColumnFormats(newColumnFormats).setColumnFormats(this.customFormats);
        }
        return tsb.build();
    }
    // Used (currently bin+outlier) when you want to copy all ins, and groupby doesn't group the rows, its just used during calculation
    // this means that the groupby doesn't restrict the outputcolumns, all columns are in the output, not just the grpby
    copyAllInsWithGroupby(exp, source, inPort, targetCol, groupByColumns) {
        const dependents = [this.getDependent(exp, inPort, targetCol)];
        const grpSet = new Set();
        if (groupByColumns)
            groupByColumns.forEach((grp) => grpSet.add(grp));
        source.forEach((colMet) => {
            if (colMet.name === targetCol || grpSet.has(colMet.name)) {
                dependents.push(colMet);
                exp.copy(inPort, colMet, false, true);
            }
            else {
                exp.copy(inPort, colMet, false, false);
            }
        });
        return dependents;
    }
    // eslint-disable-next-line class-methods-use-this
    getGroupByDependents(exp, inPort, groupByColumns, curDependents) {
        const dependents = [];
        const ignoreSet = new Set();
        if (curDependents)
            curDependents.forEach((dep) => ignoreSet.add(dep.name));
        if (groupByColumns !== undefined) {
            groupByColumns.forEach((col) => {
                const srcCol = exp.findSourceColumn(inPort, col);
                // don't readd things already in current dependents
                if (srcCol && !ignoreSet.has(col))
                    dependents.push(srcCol);
                exp.copy(inPort, srcCol, false, true);
            });
        }
        return dependents;
    }
    // Copies all of the source, setting the requiredColumns to required and returning their colmetadata as dependents
    // eslint-disable-next-line class-methods-use-this
    copyAllInsAndReturnDependents(exp, inPort, source, requiredColumns) {
        const dependents = [];
        const requiredSet = new Set();
        requiredColumns.forEach((col) => requiredSet.add(col));
        source.forEach((colMet) => {
            if (requiredSet.has(colMet.name)) {
                dependents.push(colMet);
                exp.copy(inPort, colMet, false, true);
            }
            else {
                exp.copy(inPort, colMet, false, false);
            }
        });
        return dependents;
    }
    getOutputForRun(iConfigHelper, scope, pass) {
        if (this.isValidForRun(iConfigHelper, scope, pass) !== ToolValidationState.VALID) {
            return ToolScope.empty;
        }
        if (pass) {
            if (this.outs.length > 0) {
                const tsb = new ToolScopeBuilder(scope);
                this.outs.forEach((out, i) => {
                    const exp = tsb.export(out);
                    if (this.ins.length > i && scope.schemas.has(this.ins[i])) {
                        exp.copyAll(this.ins[i]);
                    }
                });
                return tsb.build();
            }
            return ToolScope.empty;
        }
        return this.getOutput(iConfigHelper, scope);
    }
    getDependents(exp, inPort, cols) {
        const dependents = [];
        cols.forEach((col) => {
            const colMet = this.getDependent(exp, inPort, col);
            if (colMet)
                dependents.push(colMet);
        });
        return dependents;
    }
    // eslint-disable-next-line class-methods-use-this
    getDependent(exp, inPort, col) {
        return exp.findSourceColumn(inPort, col);
    }
    getOutputForCalc(iConfigHelper, scope) {
        if (this.isValidForCalc(iConfigHelper, scope) !== ToolValidationState.VALID) {
            return ToolScope.empty;
        }
        return this.getOutput(iConfigHelper, scope);
    }
    getToolValidationResult(iConfigHelper, scope) {
        // Override this method to provide a custom validation result in different tools
        return new ToolValidationResult(this.isValidForCalc(iConfigHelper, scope));
    }
    // eslint-disable-next-line class-methods-use-this
    addGroupByToInputs(inputs, groupByColumns) {
        if (groupByColumns)
            groupByColumns.forEach((col) => inputs.set(col, []));
    }
    isValidForRun(iConfigHelper, scope, pass) {
        if (pass) {
            if (this.ins.length === 0) {
                return ToolValidationState.VALID;
            }
            const passes = Math.min(this.ins.length, this.outs.length);
            for (let i = 0; i < passes; i++) {
                if (!scope.schemas.has(this.ins[i])) {
                    return ToolValidationState.BAD_METADATA;
                }
            }
            return ToolValidationState.VALID;
        }
        return this.isValidForCalc(iConfigHelper, scope);
    }
    static computeAliases(existing, columns) {
        // Compute a unique set of names, ensuring:
        //
        // 1. Names with a trailing '-####' are incremented to find new names:
        //   e.g. ['a-2'],['a-2'] -> ['a-3'], not ['a-2-2']
        // Rationale: this minimizes suffix length and is easier to understand
        //
        // 2. Only duplicated columns are renamed:
        //   e.g. ['a'],['a','a-2'] -> ['a-3','a-2'], not ['a-2','a-3']
        // Rationale: this minimizes the total number of renames
        const uniques = new Set([...existing, ...columns]);
        const aliases = new Set();
        const output = new Map();
        columns.forEach((name) => {
            let alias = name;
            if (existing.includes(name) || aliases.has(name)) {
                let suffix = 1;
                let root = name;
                const parts = name.split('-');
                if (parts.length > 1) {
                    const last = parts[parts.length - 1];
                    const numeric = /^[0-9]+$/;
                    if (numeric.exec(last) !== null) {
                        suffix = parseInt(last, 10);
                        root = name.substring(0, name.length - last.length - 1);
                    }
                }
                // eslint-disable-next-line no-constant-condition
                while (true) {
                    suffix += 1;
                    // Truncate the root to fit the suffix
                    // Includes the '-' in the suffix length
                    const suffixLength = suffix.toString().length + 1;
                    if (root.length + suffixLength > 150) {
                        root = root.substring(0, 150 - suffixLength);
                    }
                    alias = `${root}-${suffix}`;
                    if (!uniques.has(alias) && !aliases.has(alias)) {
                        break;
                    }
                }
            }
            aliases.add(alias);
            output.set(name, alias);
        });
        return output;
    }
    applyFeatureFlags(joinFeatureFlags) {
        throw new Error(`Implement applyFeatureFlags() on each toolConfig.`);
    }
    // override this for any tools that are outdated
    updateToolConfig(config) {
        return config;
    }
    // override this when a tool needs to update incoming edges when the tooltype is updated
    updateIns(edge, edgeIDs) {
        return edge;
    }
    updateOuts(edge) {
        return edge;
    }
    static columnTypesAreEqual(type1, type2) {
        if (type1 === type2) {
            return true;
        }
        const isType1Number = type1 === 'number' || type1 === 'decimal';
        const isType2Number = type2 === 'number' || type2 === 'decimal';
        if (isType1Number && isType2Number) {
            return true;
        }
        return false;
    }
};
__decorate([
    JsonConverter(new MapConverter(CustomFormat))
], ToolConfig.prototype, "customFormats", void 0);
ToolConfig = ToolConfig_1 = __decorate([
    JsonSchemaV2(toolConfigSchema, toolConfigSchemaValidation),
    JsonDiscriminatorProperty('toolType')
], ToolConfig);
export { ToolConfig };
//# sourceMappingURL=toolConfig.js.map